import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import client from "../../../api/client";
import FeatureFeedbackBox from "../../Generic/FeatureFeedbackBox";
import { fmtDay } from "../home/time";
import Icon from "./Icon";
import PageFrame, { LoadFailed, Loading } from "./PageFrame";

const REGIONS = [["nigeria", "Nigeria"], ["africa", "Africa (outside Nigeria)"], ["uk", "United Kingdom"], ["us", "United States"], ["remote_global", "Remote / Global"]];
const WORK_STYLES = [["any", "No Preference"], ["remote", "Remote"], ["hybrid", "Hybrid"], ["onsite", "On-site"]];
const SENIORITY = [["any", "No Preference"], ["entry", "Entry Level"], ["mid", "Mid Level"], ["senior", "Senior"]];
// Himalayas' terms require naming it as the source, so every posting shows where it came from.
const SOURCES = {
  e1x: "Posted by E1X Academy", jobberman: "Jobberman", myjobmag: "MyJobMag", hotnigerianjobs: "HotNigerianJobs", jobzilla: "Jobzilla",
  brightermonday: "BrighterMonday", careers24: "Careers24", himalayas: "via Himalayas", jsearch: "JSearch", linkedin: "LinkedIn", adzuna: "Adzuna", reed: "Reed",
};
// Plain site names for the source filter.
const SOURCE_NAMES = { ...SOURCES, e1x: "E1X Academy", himalayas: "Himalayas" };
const STYLE = { remote: "Remote", hybrid: "Hybrid", onsite: "On-site" };
const label = (list, v) => list.find(([k]) => k === v)?.[1] || v;
const FOR_YOU = "for_you";
const ALL = "all";

const ago = (iso) => {
  const t = Date.parse(iso);
  if (!iso || Number.isNaN(t)) return null;
  const h = Math.floor((Date.now() - t) / 3600000);
  if (h < 1) return "just now";
  if (h < 24) return `${h} ${h === 1 ? "hour" : "hours"} ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d} ${d === 1 ? "day" : "days"} ago`;
  const w = Math.floor(d / 7);
  return w < 5 ? `${w} ${w === 1 ? "week" : "weeks"} ago` : fmtDay(iso);
};
const salary = (p) => {
  if (!p.salary_min && !p.salary_max) return null;
  const n = (v) => Number(v).toLocaleString();
  return `${p.salary_currency || ""} ${p.salary_min && p.salary_max ? `${n(p.salary_min)} – ${n(p.salary_max)}` : n(p.salary_min || p.salary_max)}`.trim();
};

/** One posting (prototype `.job`). Opening a match records the click before going to the listing. */
function Job({ posting, match }) {
  const [busy, setBusy] = useState(false);
  if (!posting) return null;
  const open = async () => {
    if (match) {
      setBusy(true);
      try { await client.post(`/me/job-matches/${match.id}/click`); } catch { /* tracking must never block the listing */ }
      setBusy(false);
    }
    window.open(posting.source_url, "_blank", "noopener,noreferrer");
  };
  const facts = [posting.company, STYLE[posting.remote_type], salary(posting), ago(posting.posted_at), SOURCES[posting.source] || posting.source].filter(Boolean);
  return (
    <article className="job">
      <div className="job-logo">{(posting.company || posting.title || "?")[0].toUpperCase()}</div>
      <div className="job-main">
        <h4>{posting.title}</h4>
        <span className="sub">{facts.join(" · ")}</span>
        {/* Its own line, with the pin kept beside the text when a long location wraps. */}
        {posting.location && <span className="loc"><Icon name="pin" size={13} /><span>{posting.location}</span></span>}
      </div>
      {match ? <span className="match">{match.match_score}% match</span> : <span />}
      <button className="cbtn go" type="button" onClick={open} disabled={busy}>Apply ›</button>
    </article>
  );
}

function Prefs({ categories, initial, onSaved, onCancel }) {
  const [f, setF] = useState({
    career_path: initial?.career_path || "", category: initial?.category || "", region: initial?.region || "nigeria",
    remote_pref: initial?.remote_pref || "any", seniority_target: initial?.seniority_target || "any",
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async (e) => {
    e.preventDefault();
    if (!f.career_path) return toast.error("Choose a career path so we can match jobs to you.");
    setBusy(true);
    try {
      const res = await client.post("/me/job-preferences", f);
      toast.success("Preferences saved. Finding your matches…");
      onSaved(res.data?.data);
    } catch (err) {
      toast.error(err?.response?.data?.message || "We couldn't save your preferences. Please try again.");
    }
    setBusy(false);
    return undefined;
  };
  const select = (id, k, list) => <select id={id} value={f[k]} onChange={set(k)}>{list.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>;
  return (
    <section className="card">
      <div className="card-h"><h3>{initial ? "Edit Your Preferences" : "Tell Us What You're Looking For"}</h3></div>
      <p style={{ color: "var(--muted)" }}>We match new postings to these every week.</p>
      <form className="form two" onSubmit={save}>
        <label htmlFor="jp-path">Career path
          <select id="jp-path" value={f.career_path} onChange={set("career_path")}>
            <option value="">Choose a Career Path</option>
            {categories.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
          </select>
        </label>
        <label htmlFor="jp-role">Specific Role<input id="jp-role" value={f.category} onChange={set("category")} placeholder="e.g. Data Analyst, Backend Engineer" /><small>Optional.</small></label>
        <label htmlFor="jp-region">Region{select("jp-region", "region", REGIONS)}</label>
        <label htmlFor="jp-style">Work style{select("jp-style", "remote_pref", WORK_STYLES)}</label>
        <label htmlFor="jp-level">Seniority{select("jp-level", "seniority_target", SENIORITY)}</label>
      </form>
      <div className="cta-row">
        <button className="cbtn go" type="button" onClick={save} disabled={busy}>{busy ? "Saving…" : "Save Preferences"}</button>
        {onCancel && <button className="cbtn quiet" type="button" onClick={onCancel} disabled={busy}>Cancel</button>}
      </div>
    </section>
  );
}

/** Every live posting in a career path (or all), optionally from one site, with "Load more". */
function Browse({ path, source, onCounts, onSources }) {
  const [jobs, setJobs] = useState(null);
  const [meta, setMeta] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async (page) => {
    setBusy(true);
    try {
      const res = await client.get("/me/jobs", { params: { page, ...(path !== ALL ? { career_path: path } : {}), ...(source ? { source } : {}) } });
      const paged = res.data?.data;
      setJobs((prev) => (page === 1 ? paged?.data || [] : [...(prev || []), ...(paged?.data || [])]));
      setMeta(paged);
      if (res.data?.counts) onCounts(res.data.counts);
      if (res.data?.sources) onSources(res.data.sources);
    } catch {
      toast.error("We couldn't load jobs. Please try again.");
      setJobs((prev) => prev || []);
    }
    setBusy(false);
  }, [path, source, onCounts, onSources]);
  useEffect(() => { setJobs(null); load(1); }, [load]);

  if (!jobs) return <Loading />;
  if (!jobs.length) return <section className="complete-box"><div><strong>No jobs here yet</strong><small>We add new postings several times a week. Try another category or source, or check back soon.</small></div></section>;
  return (
    <>
      <div className="jobs">{jobs.map((j) => <Job key={j.id} posting={j} match={j.match} />)}</div>
      {meta && meta.current_page < meta.last_page && (
        <div className="cta-row" style={{ justifyContent: "center" }}>
          <button className="cbtn quiet" type="button" onClick={() => load(meta.current_page + 1)} disabled={busy}>{busy ? "Loading…" : `Load more (${meta.total - jobs.length} left)`}</button>
        </div>
      )}
    </>
  );
}

/** Job board (prototype jobsPage) on the real matching service: preferences, matches, browse by path. */
export default function JobBoardPage() {
  const [state, setState] = useState(null);
  const [prefs, setPrefs] = useState(null);
  const [categories, setCategories] = useState([]);
  const [editing, setEditing] = useState(false);
  const [tab, setTab] = useState(FOR_YOU);
  const [counts, setCounts] = useState(null);
  const [source, setSource] = useState("");
  const [sourceCounts, setSourceCounts] = useState({});

  const refresh = useCallback(async () => {
    try {
      const res = await client.get("/me/job-matches");
      setState({ eligible: res.data.eligible ?? true, needsPrefs: res.data.needs_preferences ?? false, matches: res.data.data || [] });
    } catch {
      setState({ failed: true });
    }
    client.get("/me/job-preferences").then((r) => setPrefs(r.data?.data || null)).catch(() => {});
  }, []);
  useEffect(() => {
    refresh();
    client.get("/v2/categories").then((r) => setCategories(r.data || [])).catch(() => {});
  }, [refresh]);
  useEffect(() => {
    if (!state?.eligible || state.needsPrefs) return;
    client.get("/me/jobs", { params: { page: 1 } }).then((r) => r.data?.counts && setCounts(r.data.counts)).catch(() => {});
  }, [state]);

  const head = { title: "Job Board", kicker: "Career", intro: "Roles matched to your preferences, from across the web. New postings arrive several times a week." };
  if (!state) return <PageFrame {...head}><Loading /></PageFrame>;
  if (state.failed) return <PageFrame {...head}><LoadFailed what="job board" onRetry={refresh} /></PageFrame>;
  if (!state.eligible) {
    return (
      <PageFrame {...head}>
        <section className="empty">
          <div className="empty-ico"><Icon name="brief" size={30} /></div>
          <h3>The Job Board Isn't Open to You Yet</h3>
          <p>It's for fully paid students during their cohort and for 30 days after it ends. Complete your payment, or check back once your cohort starts.</p>
        </section>
      </PageFrame>
    );
  }
  if (state.needsPrefs || editing) {
    return (
      <PageFrame {...head}>
        <Prefs categories={categories} initial={editing ? prefs : null} onCancel={editing ? () => setEditing(false) : null}
          onSaved={(p) => { setPrefs(p); setEditing(false); refresh(); }} />
      </PageFrame>
    );
  }

  const opened = state.matches.filter((m) => m.clicked_at).length;
  const pathCount = prefs?.career_path && counts ? counts[prefs.career_path] : counts?.all;
  const chips = prefs ? [
    ["Career Path", prefs.career_path], ["Role", prefs.category], ["Region", label(REGIONS, prefs.region)],
    ["Seniority", label(SENIORITY, prefs.seniority_target)], ["Work Style", label(WORK_STYLES, prefs.remote_pref)],
  ].filter(([, v]) => v) : [];
  // The source filter: For You counts the student's matches; the other tabs use the server's counts.
  const matchSources = state.matches.reduce((acc, m) => {
    const k = m.job_posting?.source;
    return k ? { ...acc, [k]: (acc[k] || 0) + 1 } : acc;
  }, {});
  const sourceOptions = Object.entries(tab === FOR_YOU ? matchSources : sourceCounts).sort(([a], [b]) => (SOURCE_NAMES[a] || a).localeCompare(SOURCE_NAMES[b] || b));
  const shownMatches = source ? state.matches.filter((m) => m.job_posting?.source === source) : state.matches;
  const tabs = [[FOR_YOU, "For You", state.matches.length], [ALL, "All Jobs", counts?.all], ...Object.keys(counts || {}).filter((k) => k !== ALL).map((k) => [k, k, counts[k]])];

  return (
    <PageFrame {...head}>
      {prefs && (
        <section className="prefs">
          <div className="pref-chips">{chips.map(([k, v]) => <span key={k}><small>{k}</small>{v}</span>)}</div>
          <button className="cbtn quiet" type="button" onClick={() => setEditing(true)}>Edit Preferences</button>
        </section>
      )}
      <div className="gsum">
        <div><b>{state.matches.length}</b><span>Picked for You</span><small>Jobs that fit your preferences</small></div>
        <div><b>{opened}</b><span>Opened</span><small>Picked jobs you've clicked</small></div>
        <div><b>{pathCount ?? "—"}</b><span>{prefs?.career_path ? `Live Jobs in ${prefs.career_path}` : "Live Jobs"}</span><small>Open right now</small></div>
      </div>
      <div className="lb-tabs chips-row" role="group" aria-label="Jobs to show">
        {tabs.map(([k, l, n]) => <button key={k} type="button" aria-pressed={tab === k} onClick={() => { setTab(k); setSource(""); }}>{l}{n != null ? ` · ${n}` : ""}</button>)}
      </div>
      {sourceOptions.length > 1 && (
        <label className="source-filter" htmlFor="jb-source">Source
          <select id="jb-source" value={source} onChange={(e) => setSource(e.target.value)}>
            <option value="">All Sources</option>
            {sourceOptions.map(([k, n]) => <option key={k} value={k}>{SOURCE_NAMES[k] || k} · {n}</option>)}
          </select>
        </label>
      )}
      {tab === FOR_YOU ? (
        shownMatches.length
          ? <div className="jobs">{shownMatches.map((m) => <Job key={m.id} posting={m.job_posting} match={m} />)}</div>
          : <section className="complete-box"><div><strong>No matches yet</strong><small>We add new postings several times a week. Browse the other categories in the meantime; your preferences are saved.</small></div></section>
      ) : <Browse path={tab} source={source} onCounts={setCounts} onSources={setSourceCounts} />}
      <FeatureFeedbackBox feature="job_board" />
    </PageFrame>
  );
}
